import { Disclosure } from '@headlessui/react';
import { useState } from 'react';
import {
  FilterCategory,
  FilterCategoryItem,
} from '../../../models/filterModal';
import { Button } from '../../atoms/Button';
import Checkbox from '../../atoms/Checkbox';
import { Icon } from '../../atoms/Icon';
import { Typography } from '../../molecules/Typography';
import { Modal } from '../../templates/Modal';

interface iListFilterModalProps {
  title: string;
  subtitle: string;
  filters: FilterCategory[];
  selectedFilters: FilterCategory[];
  setSelectedFilters: (filters: FilterCategory[]) => void;
  filterOnChange?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

function ListFilterModal({
  title,
  subtitle,
  filters,
  selectedFilters,
  setSelectedFilters,
  filterOnChange = false,
  isOpen,
  onClose,
}: iListFilterModalProps) {
  const [auxSelectedFilters, setAuxSelectedFilters] = useState(selectedFilters);

  function handleFilterClick(
    category: FilterCategory,
    item: FilterCategoryItem
  ) {
    const newSelectedFilters = [...auxSelectedFilters];

    const categoryIndex = auxSelectedFilters.findIndex(
      (filter) => filter.category_name === category.category_name
    );

    if (categoryIndex === -1 && item) {
      newSelectedFilters.push({
        ...category,
        list: [item],
      });
    } else {
      const itemIndex = auxSelectedFilters[categoryIndex].list.findIndex(
        (filterItem) => filterItem.value === item.value
      );

      if (itemIndex === -1) {
        newSelectedFilters[categoryIndex].list.push(item);
      } else {
        newSelectedFilters[categoryIndex].list.splice(itemIndex, 1);
      }

      if (newSelectedFilters[categoryIndex].list.length === 0) {
        newSelectedFilters.splice(categoryIndex, 1);
      }
    }

    setAuxSelectedFilters(newSelectedFilters);
  }

  function handleSingleFilterClick(category: FilterCategory) {
    const newSelectedFilters = [...auxSelectedFilters];

    const categoryIndex = auxSelectedFilters.findIndex(
      (filter) => filter.category_name === category.category_name
    );

    if (categoryIndex === -1) {
      newSelectedFilters.push({
        ...category,
        list: [],
        singleFilterValue: !category.singleFilterValue,
      });
    } else {
      newSelectedFilters[categoryIndex].singleFilterValue =
        !newSelectedFilters[categoryIndex].singleFilterValue;
    }

    setAuxSelectedFilters(newSelectedFilters);
  }

  function applyFilters() {
    setSelectedFilters(auxSelectedFilters);
    onClose();
  }

  function cleanFilters() {
    setSelectedFilters([]);
    setAuxSelectedFilters([]);
    onClose();
  }

  function isSelected(category: FilterCategory, item: FilterCategoryItem) {
    const categoryIndex = auxSelectedFilters.findIndex(
      (filter) => filter.category_name === category.category_name
    );

    if (categoryIndex === -1) {
      return false;
    }

    const itemIndex = auxSelectedFilters[categoryIndex].list.findIndex(
      (filterItem) => filterItem.value === item.value
    );

    return itemIndex !== -1;
  }

  function isSigleFilterSelected(category: FilterCategory) {
    const categoryIndex = auxSelectedFilters.findIndex(
      (filter) => filter.category_name === category.category_name
    );

    if (categoryIndex === -1) {
      return false;
    }

    return auxSelectedFilters[categoryIndex].singleFilterValue ?? false;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      chip={{
        children: selectedFilters.length,
        radius: 'pill',
      }}
      position='top-right'
      title={title}
      subtitle={subtitle}
      className={`
    m-0 
    mt-[10vh] 
    flex 
    min-h-[90vh]
    
    max-w-full 
    flex-col 
    rounded-b-none 
    md:m-4
    md:min-h-[calc(100%-2rem)]

    md:max-w-lg md:rounded-b-lg
  `}
    >
      {filters.map((filter, index) => {
        if (filter.isSingleFilter === true) {
          return (
            <div
              key={`filter-item-${index}`}
              className='flex items-center justify-between p-2'
              onClick={() => {
                handleSingleFilterClick(filter);
              }}
            >
              <Typography>{filter.category_name}</Typography>
              <Checkbox
                id={`filter-item-${index}`}
                label={''}
                value={filter.ref}
                checked={isSigleFilterSelected(filter)}
                onChange={() => {
                  filterOnChange && handleSingleFilterClick(filter);
                }}
                variant='rounded'
              />
            </div>
          );
        }

        return (
          <Disclosure
            key={`filter-${index}`}
            defaultOpen={filter.startOpen ?? true}
          >
            {({ open }) => (
              <>
                <Disclosure.Button className='flex w-full justify-between p-2 py-3'>
                  <Typography
                    type='title'
                    size='h4'
                    className='flex w-full items-center justify-between gap-4'
                  >
                    {filter.category_name}
                    <Icon
                      type='material'
                      Icon={'chevron_right'}
                      className={`${open ? 'rotate-90 transform' : ''}`}
                    />
                  </Typography>
                </Disclosure.Button>

                <Disclosure.Panel className='px-2 pb-3'>
                  {filter.list.map((item, index) => (
                    <div
                      key={`filter-item-${index}`}
                      className='flex items-center justify-between py-2'
                      onClick={() => {
                        handleFilterClick(filter, item);
                      }}
                    >
                      <Typography>{item.label}</Typography>
                      <Checkbox
                        id={`filter-item-${index}`}
                        label={''}
                        value={item.value.toString()}
                        checked={isSelected(filter, item)}
                        onChange={() => {
                          filterOnChange && handleFilterClick(filter, item);
                        }}
                        variant='rounded'
                      />
                    </div>
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        );
      })}

      <footer className='mt-auto flex items-center gap-lg'>
        <Button
          allowReadOnly
          variant='text'
          LeftIcon={'delete_outline'}
          onClick={cleanFilters}
          full
        >
          Limpar seleção
        </Button>
        <Button allowReadOnly onClick={applyFilters} full>
          Aplicar filtros
        </Button>
      </footer>
    </Modal>
  );
}

export { ListFilterModal };
